import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { map } from "lodash";
import queryString from "query-string";
import { useHistory, useLocation } from "react-router-dom";
import { RESOURCE_NAME } from "common/authorization";
import { FEATURE_FLAG_NAME, OBJECT_TYPE } from "common/constants";
import { INFO_PANEL_TAB_NAME } from "common/routing";
import { isNotNil } from "common/utils/tools";
import { useGetOrCreatePropertyQuery } from "../../generated/graphql";
import useNavigationTab from "../../hooks/useNavigationTabs";
import { AuthContext } from "../Authorization/AuthContext";
import TabGroup from "../Common/TabGroup";
import { LayerContext } from "../Maps/layers";
import { Tasks } from "../Tasks/List";
import {
  Container,
  Section,
  TabContainer,
  Wrapper,
} from "./__styles__/AddressPanel";
import Activity from "./Activity";
import { AddressPanelContext } from "./AddressPanelContext";
import DocumentUploads from "./DocumentUploads";
import Header from "./Header";
import { HeaderParcel } from "./Header/types";
import Overview from "./PropertyOverview";
import { Records } from "./Records";
import { AddressPanelProperty } from "./types";
import { Workflows } from "./Workflows";

export type AddressPanelTabProps = {
  address?: Maybe<string>;
  latitude: number;
  longitude: number;
};

const TabBody = (
  props: AddressPanelTabProps & {
    tab: INFO_PANEL_TAB_NAME;
    property: Maybe<AddressPanelProperty>;
  },
) => {
  const { tab, ...childProps } = props;

  return (
    <>
      {tab === INFO_PANEL_TAB_NAME.OVERVIEW && <Overview {...childProps} />}
      {tab === INFO_PANEL_TAB_NAME.ACTIVITY && <Activity {...childProps} />}
      {tab === INFO_PANEL_TAB_NAME.TASKS && (
        <Tasks
          targetObject={props.property}
          objectType={OBJECT_TYPE.PROPERTY}
        />
      )}
      {tab === INFO_PANEL_TAB_NAME.RECORDS && <Records {...childProps} />}
      {tab === INFO_PANEL_TAB_NAME.WORKFLOWS && <Workflows {...childProps} />}
      {tab === INFO_PANEL_TAB_NAME.DOCUMENT_UPLOADS && (
        <Wrapper>
          <Section>
            <DocumentUploads {...childProps} />
          </Section>
        </Wrapper>
      )}
    </>
  );
};

export interface AddressPanelProps {
  address?: Maybe<string>;
  onClose: () => void;
  longitude: number;
  latitude: number;
  propertyId?: Maybe<string>;
  parcelId?: Maybe<string>;
  parcelNumber?: Maybe<string>;
  hasPropertyIds?: Maybe<boolean>;
}

type QueryParams = {
  address?: Maybe<string>;
  lng?: Maybe<number>;
  lat?: Maybe<number>;
  tab?: string;
  propertyId?: string;
  parcelNumber?: Maybe<string>;
  parcelId?: Maybe<string>;
  hasPropertyIds?: Maybe<string>;
};

export const TABS = [
  { value: INFO_PANEL_TAB_NAME.OVERVIEW, label: "Overview" },
  { value: INFO_PANEL_TAB_NAME.ACTIVITY, label: "Activity" },
  { value: INFO_PANEL_TAB_NAME.TASKS, label: "Tasks" },
  { value: INFO_PANEL_TAB_NAME.RECORDS, label: "Records" },
  { value: INFO_PANEL_TAB_NAME.WORKFLOWS, label: "Workflows" },
  { value: INFO_PANEL_TAB_NAME.DOCUMENT_UPLOADS, label: "Files" },
];

const AddressPanel = ({
  address,
  onClose,
  longitude,
  latitude,
  propertyId,
  parcelId,
  parcelNumber,
  hasPropertyIds,
}: AddressPanelProps) => {
  const history = useHistory();
  const location = useLocation();
  const additionalData = location.state as Record<"fromSearch", boolean>;
  const { updateMap } = useContext(LayerContext);
  const { authorized, account } = useContext(AuthContext);

  const tabs = TABS.filter(tab => {
    if (tab.value === INFO_PANEL_TAB_NAME.TASKS) {
      const tasksFeature = account?.featureFlags.find(
        ff => ff.feature === FEATURE_FLAG_NAME.TASKS_WEB,
      );

      if (!tasksFeature) {
        return false;
      }

      return tasksFeature.isEnabled;
    }

    if (tab.value === INFO_PANEL_TAB_NAME.WORKFLOWS) {
      return !!account?.workflowDefinitions.length;
    }

    return true;
  });

  const defaultTab = tabs[0]!;
  const [tab, setTab] = useNavigationTab({
    defaultTab: defaultTab.value,
    allowedTabs: map(tabs, "value"),
  });

  const pushHistory = useCallback(
    (pathname: string, query: QueryParams) => {
      history.push({
        pathname,
        search: `${queryString.stringify(query)}`,
      });
    },
    [history],
  );

  const canViewRepetitiveLoss = authorized({
    resource: RESOURCE_NAME.REPETITIVE_LOSS,
    permission: "view",
  });

  const { loading: loadingProperty, data } = useGetOrCreatePropertyQuery({
    fetchPolicy: "network-only",
    variables: {
      address,
      latitude,
      longitude,
      parcelId: propertyId ? null : parcelId,
      propertyId,
      includeRepetitiveLoss: canViewRepetitiveLoss,
      improveParcelCoordinates: additionalData?.fromSearch && !propertyId,
    },
    onCompleted: ({ getProperty, parcel }) => {
      if (!propertyId && getProperty) {
        if (!hasPropertyIds) {
          updateMap();
        }
        const { id, fullAddress, latitude, longitude } = getProperty;
        const { id: parcelId, parcelNumber } = parcel || {};

        propertyId = id;

        pushHistory("/map", {
          address: fullAddress,
          lng: longitude,
          lat: latitude,
          tab: INFO_PANEL_TAB_NAME.OVERVIEW,
          propertyId: id,
          parcelNumber: parcelNumber,
          parcelId: parcelId,
        });
      }
    },
  });

  useEffect(() => {
    // when you scroll the address panel, the scroll event
    // is propagating to the background map, which is
    // not what we want, so we stop propagation here
    const swallowEvent: EventListener = (evt: Event) => {
      evt.stopPropagation();
    };

    window.addEventListener("scroll", swallowEvent, true);

    return () => window.removeEventListener("scroll", swallowEvent);
  });

  const parcel: HeaderParcel = useMemo(
    () =>
      data?.parcel || {
        parcelNumber,
        id: parcelId,
      },
    [data?.parcel, parcelId, parcelNumber],
  );

  const onPropertySelect = useCallback(
    ({
      id,
      fullAddress,
      latitude,
      longitude,
    }: {
      id: string;
      fullAddress?: Maybe<string>;
      latitude?: Maybe<number>;
      longitude?: Maybe<number>;
    }) => {
      pushHistory("/map", {
        address: fullAddress,
        lng: longitude,
        lat: latitude,
        tab: INFO_PANEL_TAB_NAME.OVERVIEW,
        propertyId: id,
        parcelNumber: parcel.parcelNumber,
        parcelId: parcel.id,
      });
    },
    [parcel, pushHistory],
  );

  const properties = [
    ...(data?.parcel?.properties || []),
    data?.getProperty,
  ].filter(isNotNil);

  const property = data?.getProperty;

  return (
    <Container>
      <Header
        property={property}
        properties={properties}
        loading={loadingProperty}
        parcel={parcel}
        latitude={latitude}
        longitude={longitude}
        onClose={onClose}
        selectProperty={onPropertySelect}
      />
      <TabContainer>
        <TabGroup currentTab={tab} setTab={setTab} tabs={tabs} />
      </TabContainer>
      <AddressPanelContext.Provider value={{ loadingProperty, property }}>
        <TabBody
          tab={tab}
          address={address}
          latitude={latitude}
          longitude={longitude}
          property={property}
        />
      </AddressPanelContext.Provider>
    </Container>
  );
};

export default React.memo(AddressPanel);
